import React from "react";
import { toast } from "react-toastify";

import Card from "../ui/Card";
import SectionCard from "../ui/SectionCard";
import Switch from "../ui/Switch";
import ConfirmModal from "../ui/ConfirmModal";
import Image from "../ui/Image";
import Accordion from "../ui/Accordion";
import Input from "../ui/Input";

import {
  formatDateDDMMYYYY,
  priceRangeLabel,
  resolveCountryCode,
  resolvePropertyCurrency,
} from "../../hooks/commonHooks";
import { getCountryName } from "../../data/countries";

import { usePropertyStore } from "../../store/propertyStore";
import {
  useDeletePropertyMutation,
  useTogglePropertyStatusMutation,
} from "../../queries/properties/usePropertiesQuery";
import {
  getPropertyMetaErrorMessage,
  usePatchPropertyMetaSocialStatusMutation,
  usePatchPropertyMetaSocialLimitMutation,
  useDeletePropertyMetaSocialLimitMutation,
} from "../../queries/properties/usePropertyMetaQuery";
import {
  useDeleteAgentBrochureAndExtractDataMutation,
  useDeleteAgentFloorPlanAndExtractDataMutation,
} from "../../queries/agents/useAgentsQuery";

import { SHOW_VOICE_AND_META } from "../../config/features";
import ExtractDataModal from "./ExtractDataModal";
import PropertyDocumentSection from "./PropertyDocumentSection";

import PropertyDetails from "./PropertyDetails";

import {
  MapPin,
  Building2,
  Calendar,
  Pencil,
  Trash2,
  Facebook,
  Instagram,
  Plus,
  Check,
  X,
} from "lucide-react";

import { useAuthStore } from "../../store/authStore";
import Button from "../ui/Button";

const META_CHANNELS = [
  {
    key: "social",
    label: "Social",
    caption: "Facebook & Instagram",
    confirmName: "Facebook & Instagram",
    Icons: [
      { Icon: Facebook, iconKey: "facebook" },
      { Icon: Instagram, iconKey: "instagram" },
    ],
    limits: [{ field: "conversation_limit", label: "Limit" }],
  },
];

function parseLimitValue(value) {
  if (value === null || value === undefined || value === "") return null;
  const parsed = Number(value);
  if (!Number.isFinite(parsed) || parsed <= 0) return null;
  return parsed;
}

function getPropertyMetaDisplay(property) {
  const social = property?.meta?.social;

  return {
    socialActive: social
      ? !!social.is_active
      : !!(property?.is_facebook && property?.is_instagram),
    conversation_limit: social
      ? social.conversation_limit
      : property?.conversation_limit,
  };
}

function LimitAmount({ value }) {
  if (value === null) {
    return <span className="property-meta-limit-amount is-empty">—</span>;
  }

  return <span className="property-meta-limit-amount">{value}</span>;
}

function LimitSummary({ limits, values }) {
  if (limits.length === 1) {
    const value = values[limits[0].field];
    return value !== null ? (
      <LimitAmount value={value} />
    ) : (
      <span className="property-meta-limit-summary-empty">No limit set</span>
    );
  }

  return (
    <span className="property-meta-limit-summary-items">
      {limits.map(({ field, label }, index) => (
        <span key={field} className="property-meta-limit-summary-item">
          {index > 0 && (
            <span className="property-meta-limit-summary-sep">·</span>
          )}
          <span>{label}</span>
          <LimitAmount value={values[field]} />
        </span>
      ))}
    </span>
  );
}

function ConversationLimitEditor({
  label,
  value,
  isAdmin,
  isEditing,
  draft,
  onDraftChange,
  onStartCreate,
  onStartEdit,
  onCancel,
  onSave,
  onDelete,
  isSaving,
}) {
  const hasValue = value !== null;

  return (
    <div className="property-meta-limit-row">
      <div className="property-meta-limit-copy">
        <span className="property-meta-limit-label">{label}</span>
        {!isEditing &&
          (hasValue ? (
            <LimitAmount value={value} />
          ) : (
            <span className="property-meta-limit-value is-empty">
              No limit set
            </span>
          ))}
      </div>
      {isEditing ? (
        <div className="property-meta-limit-editor">
          <Input
            type="number"
            min="1"
            step="1"
            value={draft}
            onChange={(event) => onDraftChange(event.target.value)}
            placeholder="Enter limit"
            className="property-meta-limit-input"
            disabled={isSaving}
            onKeyDown={(event) => {
              if (event.key === "Enter") {
                event.preventDefault();
                onSave();
              }
              if (event.key === "Escape") onCancel();
            }}
          />
          <button
            type="button"
            onClick={onSave}
            disabled={isSaving}
            aria-label={`Save ${label} limit`}
          >
            <Check size={14} />
          </button>
          <button
            type="button"
            onClick={onCancel}
            disabled={isSaving}
            aria-label={`Cancel ${label} limit edit`}
          >
            <X size={14} />
          </button>
        </div>
      ) : (
        isAdmin && (
          <div className="property-meta-limit-actions">
            {hasValue ? (
              <>
                <button
                  type="button"
                  onClick={onStartEdit}
                  disabled={isSaving}
                  aria-label={`Edit ${label} limit`}
                >
                  <Pencil size={14} />
                </button>
                <button
                  type="button"
                  onClick={onDelete}
                  disabled={isSaving}
                  aria-label={`Delete ${label} limit`}
                >
                  <Trash2 size={14} className="text-danger" />
                </button>
              </>
            ) : (
              <button
                type="button"
                onClick={onStartCreate}
                disabled={isSaving}
                aria-label={`Set ${label} limit`}
              >
                <Plus size={14} />
              </button>
            )}
          </div>
        )
      )}
    </div>
  );
}

export default function PropertyCard({ property }) {
  const { role } = useAuthStore();
  const isAdmin = role === "admin";

  const { setExtractedPdfData, setExtractedFloorPlanData } = usePropertyStore();

  const {
    id,
    title: propertyTitle,
    name,
    agent_id: propertyAgentId,
    agentId,
    area,
    city,
    state,
    country,
    pincode,
    property_type: propertyType,
    unit_type: unitTypes = [],
    min_budget,
    max_budget,
    price_range,
    expected_delivery,
    image_url,
    property_url,
    brochure_url,
    floor_plan_url,
    is_active,
    details,
    floor_plan_details,
  } = property;

  const { socialActive, conversation_limit } = getPropertyMetaDisplay(property);

  const title = propertyTitle || name || "";
  const agentIdToUse = propertyAgentId ?? agentId;
  const isActive = is_active;
  const imageUrl = typeof image_url === "string" ? image_url : null;

  const { mutate: toggleStatus, isPending: isTogglePending } =
    useTogglePropertyStatusMutation();
  const { mutate: patchSocialStatus, isPending: isSocialStatusPending } =
    usePatchPropertyMetaSocialStatusMutation();
  const { mutate: patchSocialLimit, isPending: isSocialLimitPending } =
    usePatchPropertyMetaSocialLimitMutation();
  const { mutate: deleteSocialLimit, isPending: isSocialLimitDeletePending } =
    useDeletePropertyMetaSocialLimitMutation();

  const deleteMutation = useDeletePropertyMutation();
  const { setSelectedProperty } = usePropertyStore();

  const deleteBrochureMutation = useDeleteAgentBrochureAndExtractDataMutation(
    agentIdToUse,
    id,
  );
  const deleteFloorPlanMutation = useDeleteAgentFloorPlanAndExtractDataMutation(
    agentIdToUse,
    id,
  );

  const [isPropertyDetailsOpen, setIsPropertyDetailsOpen] = React.useState(false);

  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = React.useState(false);
  const [isToggleConfirmOpen, setIsToggleConfirmOpen] = React.useState(false);
  const [isBrochureDeleteConfirmOpen, setIsBrochureDeleteConfirmOpen] =
    React.useState(false);
  const [isFloorPlanDeleteConfirmOpen, setIsFloorPlanDeleteConfirmOpen] =
    React.useState(false);
  const [isBrochureExtractDataOpen, setIsBrochureExtractDataOpen] =
    React.useState(false);
  const [isFloorPlanExtractDataOpen, setIsFloorPlanExtractDataOpen] =
    React.useState(false);
  const metaChannels = {
    social: socialActive,
  };
  const [conversationLimits, setConversationLimits] = React.useState({
    conversation_limit: parseLimitValue(conversation_limit),
  });
  const [pendingMetaChannel, setPendingMetaChannel] = React.useState(null);
  const [editingLimitField, setEditingLimitField] = React.useState(null);
  const [limitDraft, setLimitDraft] = React.useState("");
  const [pendingDeleteLimit, setPendingDeleteLimit] = React.useState(null);

  React.useEffect(() => {
    setConversationLimits({
      conversation_limit: parseLimitValue(conversation_limit),
    });
  }, [conversation_limit]);

  const isChannelStatusPending = (channelKey) =>
    channelKey === "social" ? isSocialStatusPending : false;

  const isLimitBusy = (field) =>
    field === "conversation_limit"
      ? isSocialLimitPending || isSocialLimitDeletePending
      : false;

  const handleEdit = () => setSelectedProperty(property);
  const handleDelete = () => setIsDeleteConfirmOpen(true);
  const handleToggleStatus = () => setIsToggleConfirmOpen(true);
  const handleToggleMetaChannel = (channelKey) => {
    if (isChannelStatusPending(channelKey)) return;
    setPendingMetaChannel(channelKey);
  };

  const startLimitCreate = (field) => {
    setEditingLimitField(field);
    setLimitDraft("");
  };

  const startLimitEdit = (field) => {
    setEditingLimitField(field);
    setLimitDraft(String(conversationLimits[field] ?? ""));
  };

  const cancelLimitEdit = () => {
    setEditingLimitField(null);
    setLimitDraft("");
  };

  const saveLimit = (field, label) => {
    const trimmed = String(limitDraft ?? "").trim();
    const parsed = Number(trimmed);
    if (!trimmed || !Number.isInteger(parsed) || parsed < 1) {
      toast.error("Enter a positive whole number");
      return;
    }

    const mutate = patchSocialLimit;

    mutate(
      {
        agentId: agentIdToUse,
        propertyId: id,
        data: { [field]: parsed },
      },
      {
        onSuccess: () => {
          setConversationLimits((prev) => ({
            ...prev,
            [field]: parsed,
          }));
          toast.success(`${label} limit saved: ${parsed}`);
          cancelLimitEdit();
        },
        onError: (error) => {
          toast.error(
            getPropertyMetaErrorMessage(
              error,
              "Failed to save conversation limit",
            ),
          );
        },
      },
    );
  };

  const confirmDeleteLimit = () => {
    if (!pendingDeleteLimit) return;

    const mutate = deleteSocialLimit;

    mutate(
      { agentId: agentIdToUse, propertyId: id },
      {
        onSuccess: () => {
          setConversationLimits((prev) => ({
            ...prev,
            [pendingDeleteLimit.field]: null,
          }));
          toast.success(`${pendingDeleteLimit.label} limit removed`);
          setPendingDeleteLimit(null);
          if (editingLimitField === pendingDeleteLimit.field) {
            cancelLimitEdit();
          }
        },
        onError: (error) => {
          toast.error(
            getPropertyMetaErrorMessage(
              error,
              "Failed to delete conversation limit",
            ),
          );
        },
      },
    );
  };

  const confirmDelete = () => {
    deleteMutation.mutate({ agentId: agentIdToUse, propertyId: id });
    setIsDeleteConfirmOpen(false);
  };

  const confirmToggle = () => {
    toggleStatus({ agentId: agentIdToUse, propertyId: id });
    setIsToggleConfirmOpen(false);
  };

  const pendingMetaConfig = META_CHANNELS.find(
    (channel) => channel.key === pendingMetaChannel,
  );
  const pendingMetaActive = pendingMetaChannel
    ? metaChannels[pendingMetaChannel]
    : false;

  const confirmMetaToggle = () => {
    if (!pendingMetaChannel) return;
    const next = !pendingMetaActive;
    const mutate = patchSocialStatus;

    mutate(
      {
        agentId: agentIdToUse,
        propertyId: id,
        data: { is_active: next },
      },
      {
        onSuccess: () => {
          toast.success(
            next
              ? `${pendingMetaConfig?.confirmName} activated`
              : `${pendingMetaConfig?.confirmName} deactivated`,
          );
          setPendingMetaChannel(null);
        },
        onError: (error) => {
          toast.error(
            getPropertyMetaErrorMessage(
              error,
              "Failed to update channel status",
            ),
          );
        },
      },
    );
  };

  const confirmBrochureDelete = () => {
    deleteBrochureMutation.mutate(undefined, {
      onSuccess: () => {
        if (isAdmin) {
          setExtractedPdfData(null);
        }
        setIsBrochureDeleteConfirmOpen(false);
      },
    });
  };

  const confirmFloorPlanDelete = () => {
    deleteFloorPlanMutation.mutate(undefined, {
      onSuccess: () => {
        if (isAdmin) {
          setExtractedFloorPlanData(null);
        }
        setIsFloorPlanDeleteConfirmOpen(false);
      },
    });
  };

  let unitTypesToDisplay;

  if (propertyType !== "plot") {
    unitTypesToDisplay = (
      <div className="unit-types mt-2">
        {(Array.isArray(unitTypes) ? unitTypes : []).map((unit) => (
          <span key={unit} className="unit-badge">
            {unit !== null ? unit : "N/A"}
          </span>
        ))}
      </div>
    );
  }

  return (
    <Card className="property-card m-0 p-0 position-relative test-123">
      {isAdmin && (
        <div className="property-actions">
          <button onClick={handleEdit}>
            <Pencil size={18} />
          </button>
          <button onClick={handleDelete} disabled={deleteMutation.isPending}>
            <Trash2 size={18} className="text-danger" />
          </button>
        </div>
      )}

      <div className="property-card-header  d-flex flex-column gap-2">
        <div className="property-image-wrapper">
          <Image
            src={imageUrl}
            alt={title || "Property image"}
            className="property-image"
          />
        </div>
        <div className="px-2 d-flex align-items-center justify-between">
          <h3 className="property-title">{title || "Untitled Property"}</h3>
          {isAdmin && (
            <Switch
              checked={isActive}
              onChange={handleToggleStatus}
              disabled={isTogglePending}
            />
          )}
        </div>

        <div className="property-summary px-2 d-flex justify-between align-center gap-1">
          <div className="property-item">
            <MapPin size={16} />
            <span>
              {city || "—"}, {area || "—"}
            </span>
          </div>

          <div className="property-item gap-0">
            <span className="text-primary text-nowrap">
              {priceRangeLabel(
                min_budget,
                max_budget,
                price_range,
                resolvePropertyCurrency(property),
              )}
            </span>
          </div>
        </div>
      </div>

      <Accordion title="Location Details" className="px-2">
        <div className="property-grid">
          <div className="property-item">
            <MapPin size={16} />
            <span>Area: {area || "—"}</span>
          </div>
          <div className="property-item">
            <MapPin size={16} />
            <span>City: {city || "—"}</span>
          </div>
          <div className="property-item">
            <MapPin size={16} />
            <span>State: {state || "—"}</span>
          </div>
          <div className="property-item">
            <MapPin size={16} />
            <span>
              Country:{" "}
              {getCountryName(resolveCountryCode(property)) || country || "—"}
            </span>
          </div>
          <div className="property-item">
            <MapPin size={16} />
            <span>Pincode: {pincode || "—"}</span>
          </div>
        </div>
      </Accordion>

      <Accordion title="Property Details" className="px-2">
        <div className="property-grid">
          <div className="property-item">
            <Building2 size={16} />
            <span>Type: {propertyType || "—"}</span>
          </div>

          <div className="property-item">
            <Calendar size={16} />
            <span>Delivery: {formatDateDDMMYYYY(expected_delivery)}</span>
          </div>
        </div>

        {unitTypesToDisplay}
      </Accordion>

      <Accordion title="Links" className="px-2">
        {property_url && (
          <div className="mb-2 overflow-hidden">
            <strong>Property URL:</strong>
            <br />
            <a href={property_url} target="_blank" rel="noreferrer">
              {property_url}
            </a>
          </div>
        )}
      </Accordion>

      {SHOW_VOICE_AND_META && (
      <Accordion title="Meta" className="px-2">
        <div className="property-meta-list">
          {META_CHANNELS.map(({ key, label, caption, Icons, limits }) => {
            const isChannelActive = metaChannels[key];
            return (
              <div
                key={key}
                className={`property-meta-row is-${key} ${isChannelActive ? "is-active" : "is-inactive"}`}
              >
                <div className="property-meta-header">
                  <div className="property-meta-identity">
                    <span
                      className={`property-meta-icon-wrap ${Icons.length > 1 ? "is-stack" : ""}`}
                      aria-hidden="true"
                    >
                      {Icons.map(({ Icon, iconKey }) => (
                        <span
                          key={iconKey}
                          className={`property-meta-icon is-${iconKey}`}
                        >
                          <Icon size={16} strokeWidth={2.15} />
                        </span>
                      ))}
                    </span>
                    <div className="property-meta-copy">
                      <span className="property-meta-label">{label}</span>
                      {caption && (
                        <span className="property-meta-caption">{caption}</span>
                      )}
                    </div>
                  </div>
                  <div className="property-meta-controls">
                    <span
                      className={`property-meta-status ${isChannelActive ? "active" : "inactive"}`}
                    >
                      <span className="property-meta-status-dot" />
                      {isChannelActive ? "Active" : "Inactive"}
                    </span>
                    {isAdmin && (
                      <Switch
                        checked={isChannelActive}
                        onChange={() => handleToggleMetaChannel(key)}
                        disabled={isChannelStatusPending(key)}
                      />
                    )}
                  </div>
                </div>
                <Accordion
                  title={
                    <span className="property-meta-limit-accordion-title">
                      <span>Conversation Limit</span>
                      <span className="property-meta-limit-summary">
                        <LimitSummary
                          limits={limits}
                          values={conversationLimits}
                        />
                      </span>
                    </span>
                  }
                  defaultOpen
                  className="property-meta-limit-accordion"
                >
                  <div className="property-meta-limits">
                    {limits.map((limit) => (
                      <ConversationLimitEditor
                        key={limit.field}
                        label={limit.label}
                        value={conversationLimits[limit.field]}
                        isAdmin={isAdmin}
                        isEditing={editingLimitField === limit.field}
                        draft={limitDraft}
                        onDraftChange={setLimitDraft}
                        onStartCreate={() => startLimitCreate(limit.field)}
                        onStartEdit={() => startLimitEdit(limit.field)}
                        onCancel={cancelLimitEdit}
                        onSave={() => saveLimit(limit.field, limit.label)}
                        onDelete={() => setPendingDeleteLimit(limit)}
                        isSaving={isLimitBusy(limit.field)}
                      />
                    ))}
                  </div>
                </Accordion>
              </div>
            );
          })}
        </div>
      </Accordion>
      )}

      {(brochure_url || floor_plan_url) && (
        <Accordion title="Documents & Extracted Data" className="px-2">
          <div className="property-doc-list">
            {brochure_url && (
              <PropertyDocumentSection
                label="Brochure"
                variant="brochure"
                url={brochure_url}
                hasExtractData={!!details}
                isAdmin={isAdmin}
                onUpdateExtract={() => setIsBrochureExtractDataOpen(true)}
                onDelete={() => setIsBrochureDeleteConfirmOpen(true)}
                isDeletePending={deleteBrochureMutation.isPending}
                viewerTitle="Brochure"
              />
            )}
            {floor_plan_url && (
              <PropertyDocumentSection
                label="Floor Plan"
                variant="floor-plan"
                url={floor_plan_url}
                hasExtractData={!!floor_plan_details}
                isAdmin={isAdmin}
                onUpdateExtract={() => setIsFloorPlanExtractDataOpen(true)}
                onDelete={() => setIsFloorPlanDeleteConfirmOpen(true)}
                isDeletePending={deleteFloorPlanMutation.isPending}
                viewerTitle="Floor Plan"
              />
            )}
          </div>
        </Accordion>
      )}

      <Accordion title="Features & Amenities" className="px-2  property-details-section">
        <div className="property-details-grid">
          <div className="property-details-card">
            <Button
              type="button"
              variant="secondary"
              size="md"
              onClick={() => setIsPropertyDetailsOpen(true)}
            >
              View Amenities
            </Button>
          </div>
        </div>
      </Accordion>

      <div className="property-footer px-2 py-2">
        <span className={`status-badge ${isActive ? "active" : "inactive"}`}>
          {isActive ? "Active" : "Inactive"}
        </span>
      </div>

      <ConfirmModal
        isOpen={isDeleteConfirmOpen}
        title="Delete Property"
        message={`Are you sure you want to delete "${title}"?`}
        confirmLabel="Delete"
        cancelLabel="Cancel"
        onConfirm={confirmDelete}
        onCancel={() => setIsDeleteConfirmOpen(false)}
        isLoading={deleteMutation.isPending}
        confirmVariant="danger"
      />

      <ConfirmModal
        isOpen={isToggleConfirmOpen}
        title={isActive ? "Deactivate Property" : "Activate Property"}
        message={
          isActive
            ? "This property will no longer be visible to leads."
            : "This property will be marked as active."
        }
        confirmLabel={isActive ? "Deactivate" : "Activate"}
        cancelLabel="Cancel"
        onConfirm={confirmToggle}
        onCancel={() => setIsToggleConfirmOpen(false)}
        isLoading={isTogglePending}
        confirmVariant="primary"
      />

      <ConfirmModal
        isOpen={!!pendingMetaChannel}
        title={
          pendingMetaActive
            ? `Deactivate ${pendingMetaConfig?.confirmName}`
            : `Activate ${pendingMetaConfig?.confirmName}`
        }
        message={
          pendingMetaActive
            ? `This property will no longer be visible on ${pendingMetaConfig?.confirmName}.`
            : `This property will be marked as active on ${pendingMetaConfig?.confirmName}.`
        }
        confirmLabel={pendingMetaActive ? "Deactivate" : "Activate"}
        cancelLabel="Cancel"
        onConfirm={confirmMetaToggle}
        onCancel={() => setPendingMetaChannel(null)}
        isLoading={isChannelStatusPending(pendingMetaChannel)}
        confirmVariant="primary"
      />

      <ConfirmModal
        isOpen={!!pendingDeleteLimit}
        title={`Delete ${pendingDeleteLimit?.label} Limit`}
        message={`Are you sure you want to remove the ${pendingDeleteLimit?.label?.toLowerCase()} conversation limit?`}
        confirmLabel="Delete"
        cancelLabel="Cancel"
        onConfirm={confirmDeleteLimit}
        onCancel={() => setPendingDeleteLimit(null)}
        isLoading={isLimitBusy(pendingDeleteLimit?.field)}
        confirmVariant="danger"
      />

      <ConfirmModal
        isOpen={isBrochureDeleteConfirmOpen}
        title="Delete Brochure"
        message="Are you sure you want to delete the brochure and its extracted data?"
        confirmLabel="Delete"
        cancelLabel="Cancel"
        onConfirm={confirmBrochureDelete}
        onCancel={() => setIsBrochureDeleteConfirmOpen(false)}
        isLoading={deleteBrochureMutation.isPending}
        confirmVariant="danger"
      />

      <ConfirmModal
        isOpen={isFloorPlanDeleteConfirmOpen}
        title="Delete Floor Plan"
        message="Are you sure you want to delete the floor plan and its extracted data?"
        confirmLabel="Delete"
        cancelLabel="Cancel"
        onConfirm={confirmFloorPlanDelete}
        onCancel={() => setIsFloorPlanDeleteConfirmOpen(false)}
        isLoading={deleteFloorPlanMutation.isPending}
        confirmVariant="danger"
      />

      <ExtractDataModal
        details={details}
        isOpen={isBrochureExtractDataOpen}
        onClose={() => setIsBrochureExtractDataOpen(false)}
        title="Extracted Brochure Data"
      />

      <ExtractDataModal
        details={floor_plan_details}
        isOpen={isFloorPlanExtractDataOpen}
        onClose={() => setIsFloorPlanExtractDataOpen(false)}
        title="Extracted Floor Plan Data"
      />

      <PropertyDetails
        PropertyDetails={details}
        isOpen={isPropertyDetailsOpen}
        onClose={() => setIsPropertyDetailsOpen(false)}
      />
    </Card>
  );
}
